import React, { useMemo } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import { getLength, getPointAtLength } from '@remotion/paths';
import { HERO_ACCENT, type HeroThread } from '../schema';
import { threadPath, threadState } from '../timeline';

const TAIL_SEGMENTS = 6;
const CORE_WIDTH = 5;
const GLOW_WIDTH = 18;

/**
 * The signature "blue demand thread": a luminous Lochmara line travelling along the clip's spline with a brighter
 * head spark and a fading tail. Purely a function of the current frame (no CSS transitions).
 */
export const DemandThread: React.FC<{ thread: HeroThread; durationInFrames: number }> = ({ thread, durationInFrames }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const ids = useMemo(() => {
    const key = `thread-${Math.round(thread.points[0]!.x * 1000)}-${Math.round(thread.points[0]!.y * 1000)}-${thread.points.length}`;
    return { glow: `${key}-glow`, spark: `${key}-spark`, halo: `${key}-halo` };
  }, [thread.points]);
  const path = useMemo(() => threadPath(thread.points, width, height), [thread.points, width, height]);
  const length = useMemo(() => (path ? getLength(path) : 0), [path]);

  const state = threadState(frame, thread, durationInFrames, fps);
  if (!path || length <= 0 || (!state.visible && state.sparkOpacity <= 0)) return null;

  const headLen = length * state.head;
  const tailLen = length * state.tail;
  const span = headLen - tailLen;
  const segments = Array.from({ length: TAIL_SEGMENTS }, (_, i) => {
    const a = tailLen + (span * i) / TAIL_SEGMENTS;
    const b = tailLen + (span * (i + 1)) / TAIL_SEGMENTS;
    return { a, b, opacity: (i + 1) / TAIL_SEGMENTS };
  });
  const head = getPointAtLength(path, Math.min(length, headLen));
  if (!head) return null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
    >
      <defs>
        <filter id={ids.glow} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <filter id={ids.halo} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="10" />
        </filter>
        <radialGradient id={ids.spark}>
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.4" stopColor="#9FD6FF" />
          <stop offset="1" stopColor={HERO_ACCENT} stopOpacity="0" />
        </radialGradient>
      </defs>
      {state.visible
        ? segments.map((s, i) => (
            <React.Fragment key={i}>
              <path
                d={path}
                fill="none"
                stroke={HERO_ACCENT}
                strokeWidth={GLOW_WIDTH}
                strokeLinecap="round"
                strokeDasharray={`${Math.max(0, s.b - s.a)} ${length}`}
                strokeDashoffset={-s.a}
                opacity={0.55 * s.opacity}
                filter={`url(#${ids.glow})`}
              />
              <path
                d={path}
                fill="none"
                stroke={HERO_ACCENT}
                strokeWidth={CORE_WIDTH}
                strokeLinecap="round"
                strokeDasharray={`${Math.max(0, s.b - s.a)} ${length}`}
                strokeDashoffset={-s.a}
                opacity={s.opacity}
              />
              {i >= TAIL_SEGMENTS - 2 ? (
                <path
                  d={path}
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeDasharray={`${Math.max(0, s.b - s.a)} ${length}`}
                  strokeDashoffset={-s.a}
                  opacity={0.7 * s.opacity}
                />
              ) : null}
            </React.Fragment>
          ))
        : null}
      {state.sparkOpacity > 0 ? (
        <g opacity={state.sparkOpacity}>
          <circle cx={head.x} cy={head.y} r={26} fill={HERO_ACCENT} opacity={0.45} filter={`url(#${ids.halo})`} />
          <circle cx={head.x} cy={head.y} r={11} fill={`url(#${ids.spark})`} />
        </g>
      ) : null}
    </svg>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
